import { useState, useEffect } from 'react'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'
import './CheckoutModal.css'

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000'
const fmt = (n) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })

// ── Coordonnées bancaires du destinataire ─────────────────────────────────────
const BANK_INFO = {
  titulaire: 'DENISE CAROLINE TURCK',
  iban:      'FR76 1723 8000 0100 3665 1346 747',
  bic:       'SCSYFRP2',
  rib:       '17238 00001 00366513467 47',
}

/* ── Formulaire virement ── */
function WireForm({ items, total, wireReference, onConfirm, onCancel, loading }) {
  return (
    <div className="checkout-wire">
      <p className="cw-intro">
        Veuillez effectuer un virement vers le compte ci-dessous en indiquant
        la référence exacte dans le libellé. Votre commande sera confirmée
        dès réception du paiement.
      </p>

      {/* Titre + Tableau bancaire */}
      <div className="cw-section-title">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="10" width="18" height="11" rx="1"/>
          <path d="M3 10l9-7 9 7"/>
        </svg>
        Effectuez votre virement bancaire
      </div>
      <div className="cw-table">

        <div className="cw-row">
          <div className="cw-cell-label">Titulaire</div>
          <div className="cw-cell-value cw-bold">{BANK_INFO.titulaire}</div>
        </div>

        <div className="cw-row">
          <div className="cw-cell-label">IBAN</div>
          <div className="cw-cell-value cw-bold">{BANK_INFO.iban}</div>
        </div>

        <div className="cw-row">
          <div className="cw-cell-label">BIC / SWIFT</div>
          <div className="cw-cell-value cw-bold">{BANK_INFO.bic}</div>
        </div>

        <div className="cw-row">
          <div className="cw-cell-label">RIB</div>
          <div className="cw-cell-value cw-bold">{BANK_INFO.rib}</div>
        </div>

        {/* Ligne référence — fond doré */}
        <div className="cw-row cw-row-ref">
          <div className="cw-cell-label cw-label-ref">Référence</div>
          <div className="cw-cell-value cw-ref">
            {loading ? <span className="cw-ref-loading">Génération…</span> : wireReference || '—'}
          </div>
        </div>

        <div className="cw-row cw-row-last">
          <div className="cw-cell-label">Montant exact</div>
          <div className="cw-cell-value cw-amount">{fmt(total)}</div>
        </div>

      </div>

      {/* Notice — icône triangle danger ⚠️ */}
      <div className="cw-notice">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:1}}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        <span>
          Indiquez impérativement la référence{' '}
          <strong>{loading ? '…' : wireReference || '—'}</strong>{' '}
          dans le libellé de votre virement.
          Bien vouloir respecter le nom du titulaire du compte ainsi que l'IBAN mentionné lors de votre virement
        </span>
      </div>

      {/* Actions */}
      <div className="checkout-actions" style={{ marginTop: 20 }}>
        <button className="checkout-cancel" onClick={onCancel} disabled={loading}>
          Annuler
        </button>
        <button className="checkout-pay-btn" onClick={onConfirm} disabled={loading || (!wireReference && !loading)}>
          {loading
            ? <><span className="checkout-spinner" /> Préparation…</>
            : <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                J'ai effectué mon virement
              </>
          }
        </button>
      </div>

      <div className="checkout-secure">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        Virement bancaire sécurisé
      </div>
    </div>
  )
}

/* ── Modal principale ── */
export default function CheckoutModal({
  isOpen    = false,
  items     = [],
  onClose   = () => {},
  onSuccess = () => {},
}) {
  const [step, setStep]               = useState('checkout')
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState('')
  const [paidOrders, setPaidOrders]   = useState([])
  const [wireReference, setWireReference] = useState('')
  const [isFree, setIsFree]           = useState(false)
  const [freeLoading, setFreeLoading] = useState(false)

  const total = items.reduce((s, i) => s + i.price, 0)

  useBodyScrollLock(isOpen)

  // Réinitialiser + générer la référence localement à l'ouverture
  // La commande N'EST PAS créée ici — seulement au clic "J'ai effectué mon virement"
  useEffect(() => {
    if (!isOpen) return
    setStep('checkout')
    setError('')
    setPaidOrders([])
    setLoading(false)

    const isFreeOrder = total === 0
    setIsFree(isFreeOrder)

    // Générer une référence locale pour affichage immédiat (pas d'appel API)
    const year = new Date().getFullYear()
    const rand = Math.random().toString(36).toUpperCase().slice(2, 8)
    setWireReference(`VIR-${year}-${rand}`)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setStep('checkout')
    setError('')
    setWireReference('')
    setPaidOrders([])
    setIsFree(false)
    onClose()
  }

  // Virement — crée la commande UNIQUEMENT au clic "J'ai effectué mon virement"
  const handleWireConfirm = async () => {
    setLoading(true)
    setError('')
    try {
      const token = localStorage.getItem('token')
      const results = []
      for (const item of items) {
        const res = await fetch(`${API}/api/orders`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({
            listingId:     item.id,
            paymentMethod: 'wire',
            wireReference,
          }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message)
        results.push(data)
        if (data.wireReference && data.wireReference !== wireReference) {
          setWireReference(data.wireReference)
        }
      }
      const orders = results.map(r => r.order)
      setPaidOrders(orders)
      onSuccess({ orders })
      onClose()
    } catch (err) {
      setError(err.message || 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  // Don gratuit — confirmation immédiate
  const handleFreeConfirm = async () => {
    setFreeLoading(true)
    setError('')
    try {
      const token = localStorage.getItem('token')
      const results = []
      for (const item of items) {
        const res = await fetch(`${API}/api/orders`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ listingId: item.id, paymentMethod: 'free' }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message)
        results.push(data)
      }
      const orders = results.map(r => r.order)
      setPaidOrders(orders)
      onSuccess({ orders })
      onClose()
    } catch (err) {
      setError(err.message || 'Erreur lors de la confirmation')
    } finally {
      setFreeLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="checkout-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="checkout-modal">

        {/* Header */}
        <div className="checkout-header">
          <div className="checkout-header-logo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <div className="checkout-header-title">
            {step === 'success'
              ? (isFree ? 'Adoption confirmée !' : 'Virement enregistré !')
              : (isFree ? "Confirmer l'adoption" : 'Finaliser la commande')}
          </div>
          <button className="checkout-close" onClick={handleClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Contenu */}
        <div className="checkout-body">

          {/* ── SUCCÈS ── */}
          {step === 'success' && (
            <div className="checkout-success">
              <div className="checkout-success-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <div className="checkout-success-title">
                {isFree ? 'Adoption confirmée !' : 'Virement enregistré !'}
              </div>
              <div className="checkout-success-sub">
                {isFree
                  ? 'Votre adoption a été confirmée. Retrouvez votre document dans votre espace personnel.'
                  : `Votre commande est enregistrée. Effectuez votre virement avec la référence ${wireReference || '—'} et elle sera confirmée sous 1–3 jours ouvrés.`}
              </div>
              {!isFree && wireReference && (
                <div className="checkout-wire-ref-banner">
                  Référence à indiquer : <strong>{wireReference}</strong>
                </div>
              )}
              <div className="checkout-success-orders">
                {paidOrders.map((o, i) => (
                  <div key={i} className="checkout-success-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    {o.listingSnapshot?.title || 'Article'} — {fmt(o.amount)}
                  </div>
                ))}
              </div>
              <button className="checkout-pay-btn" onClick={handleClose} style={{ marginTop: 24, width: '100%' }}>
                Voir mes achats →
              </button>
            </div>
          )}

          {/* ── PAIEMENT ── */}
          {step === 'checkout' && (
            <>
              {error && (
                <div className="checkout-error">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              {/* Don gratuit */}
              {isFree && (
                <div className="checkout-free">
                  <div className="checkout-free-icon">🐾</div>
                  <div className="checkout-free-title">Adoption gratuite</div>
                  <div className="checkout-free-sub">
                    Cette annonce est proposée en don. Aucun paiement requis.
                    Confirmez votre demande ci-dessous.
                  </div>
                  <div className="checkout-recap" style={{ marginTop: 16 }}>
                    <div className="checkout-recap-title">Récapitulatif</div>
                    {items.map(item => (
                      <div key={item.id} className="checkout-recap-item">
                        <img src={item.img} alt={item.title} className="checkout-recap-img" />
                        <div className="checkout-recap-info">
                          <div className="checkout-recap-name">{item.title}</div>
                          <div className="checkout-recap-breed">{item.breed} · {item.city}</div>
                        </div>
                        <div className="checkout-recap-price" style={{ color: '#1B6B3A' }}>Gratuit</div>
                      </div>
                    ))}
                  </div>
                  <div className="checkout-actions" style={{ marginTop: 20 }}>
                    <button className="checkout-cancel" onClick={handleClose} disabled={freeLoading}>
                      Annuler
                    </button>
                    <button className="checkout-pay-btn" onClick={handleFreeConfirm} disabled={freeLoading}
                      style={{ background: '#1B6B3A' }}>
                      {freeLoading
                        ? <><span className="checkout-spinner" /> Confirmation...</>
                        : <>🐾 Confirmer l'adoption</>}
                    </button>
                  </div>
                </div>
              )}

              {/* Virement bancaire */}
              {!isFree && (
                <WireForm
                  items={items}
                  total={total}
                  wireReference={wireReference}
                  onConfirm={handleWireConfirm}
                  onCancel={handleClose}
                  loading={loading}
                />
              )}
            </>
          )}

        </div>
      </div>
    </div>
  )
}